/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import _ from 'underscore';
import moment from 'moment-timezone';
import config from 'js-sdk/src/jrs.configs';
import i18n from '../../i18n/AdHocBundle.properties';
import formatsMapping from './formatsMapping';
import { Categorizer, Options } from './types';

const timezoneRegExp = /[+-]\d{2}:?\d{2}$/;

const getTodayDate = () => {
    return moment().format('YYYY-MM-DD');
};

function convertTimeToIso(value: string): string {
    // If value already ISO formatted just return it.
    if (/T/.test(value)) {
        return value;
    }
    return `${getTodayDate()}T${value}`;
}

function getMappingForLocale(locale: string) {
    let mapping = formatsMapping[locale];
    if (!mapping) {
        mapping = formatsMapping[locale.split('_')[0]];
    }
    if (!mapping) {
        mapping = formatsMapping.en;
    }
    return mapping;
}

function parseOptions(options?: Options): string {
    let result;
    // backwards compatibility.
    if (options && _.isObject(options) && (options as Categorizer).categorizer) {
        result = (options as Categorizer).categorizer;
    } else {
        result = options || 'none';
    }
    if (typeof result === 'string') {
        return result.split('|')[0];
    }
    return result;
}

function getFormatForMoment(format?: string, options?: Options) {
    if (!format) {
        return null;
    }
    const mappingForLocale = getMappingForLocale(config.userLocale);
    // Categorizers may contain a pipe character followed by a number indicating the start of a fiscal year
    // we must split this out to get the proper mapping
    const categorizer = parseOptions(options);
    if (categorizer?.indexOf('_only') > 0) {
        if (categorizer.startsWith('quarter_only')) return '[Q]Q';
        if (categorizer.startsWith('month_only')) return 'MMMM';
    }
    if (!mappingForLocale) {
        return null;
    }
    if (!mappingForLocale[categorizer]) {
        return null;
    }
    return mappingForLocale[categorizer][format];
}

function shouldBeFormattedByDateFormatter(options?: Options): boolean {
    return _.contains([
        'year',
        'quarter',
        'quarter_only',
        'month',
        'month_only',
        'day'
    ], parseOptions(options));
}

function shouldBeReturnedAsLocalizedDayOfWeek(options?: Options): boolean {
    return parseOptions(options) === 'day_of_week';
}

function getLocalizedDayOfWeek(value: string): string {
    return i18n[`adhoc.day.of.week.${value}`] || value;
}

function setToUserTimezone(value: string) {
    if (/^Q[1-4]{1}$/.test(value)
        || /^[A-Za-z]*$/.test(value)) {
        return value; // ignore Quarter (Q1, Q2..) and Month (January, February..) values (they don't have time)
    }
    let userTimezoneOffset :string;
    let result = value;
    if (/T/.test(value)) {
        userTimezoneOffset = moment(value).tz(config.userTimezone).format('Z');
    } else {
        userTimezoneOffset = moment(getTodayDate()).tz(config.userTimezone).format('Z');
    }
    if (timezoneRegExp.test(value)) {
        result = value.replace(timezoneRegExp, userTimezoneOffset);
    } else {

        if (result[result.length - 1] === 'Z') {
            result = result.substr(0, result.length - 1);
        }

        // remember: this is a string concatenation, not a number
        result += userTimezoneOffset;
    }
    return result;
}

function ensureTimezone(value: string) {
    if (timezoneRegExp.test(value)) {
        return value;
    }

    let userTimezoneOffset :string;
    if (/T/.test(value)) {
        userTimezoneOffset = moment(value).tz(config.userTimezone).format('Z');
    } else {
        userTimezoneOffset = moment(getTodayDate()).tz(config.userTimezone).format('Z');
    }

    if (value[value.length - 1] === 'Z') {
        // eslint-disable-next-line no-param-reassign
        value = value.substr(0, value.length - 1);
    }

    // remember: this is a string concatenation, not a number
    return value + userTimezoneOffset;
}

function isEmpty(value: string): boolean {
    return value === '' || value === null;
}

function isOtherNode(value: string): boolean {
    return value === i18n['adhoc.node.other.node'];
}

function isTotalNode(value: string): boolean {
    return value === i18n['adhoc.node.total.node'];
}

export default {
    getTodayDate,
    convertTimeToIso,
    getFormatForMoment,
    getLocalizedDayOfWeek,
    setToUserTimezone,
    ensureTimezone,
    shouldBeFormattedByDateFormatter,
    shouldBeReturnedAsLocalizedDayOfWeek,
    isEmpty,
    isOtherNode,
    isTotalNode
};
